/**
 * Decides how an iOS/web "upload" dropzone tap should behave with respect to the phone-verification
 * gate.
 *
 * Why this exists: the iOS/web upload control is a hidden `<input type="file">` inside a `<label>`.
 * On iOS WKWebView a **programmatic** `input.click()` on a `display:none` file input does NOT open
 * the picker — the picker only opens from the native `<label>`→`<input>` user-activation. So we
 * cannot gate the picker by calling `requirePhone(() => input.click())`: once the verification modal
 * sits between the tap and the click, the click has no user gesture and the picker never appears
 * (the reported bug — "after I verified, upload doesn't bring up photos").
 *
 * The fix: don't intercept verified users at all — let their native label→input activation open the
 * picker. Only intercept UNVERIFIED users to open the gate; after verifying they tap Upload again.
 *
 * @returns `true` if the tap was intercepted for the gate (the caller should `preventDefault()` and
 *          NOT let the native picker open); `false` to let the native label→input picker proceed.
 */
export const gateWebUploadTap = (
    hasVerifiedPhone: boolean,
    requirePhone: (onSuccess: () => void) => void,
    onAfterVerify: () => void,
): boolean => {
    if (hasVerifiedPhone) {
        return false; // verified → let the native <label>→<input> activation open the picker
    }
    requirePhone(onAfterVerify); // unverified → open the gate modal
    return true; // intercepted → caller preventDefaults
};
